import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  AlertTriangle,
  ChevronLeft,
  Loader2,
  CheckCircle2,
  Phone,
  Mail,
  Search,
  Filter,
  Clock,
  User,
  MapPin,
  Award,
  RefreshCw,
  Calendar,
  Users,
  XCircle,
  PhoneCall
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * Shift Coverage Dashboard
 * 
 * For office staff to:
 * - View reported callouts
 * - Find replacement caregivers with AI matching
 * - Call or email caregivers directly
 * - Track coverage status
 */

const COVERAGE_STATUS_CONFIG = {
  uncovered: { label: 'Uncovered', color: 'bg-red-100 text-red-700', icon: AlertTriangle },
  searching: { label: 'Searching', color: 'bg-amber-100 text-amber-700', icon: Search },
  covered: { label: 'Covered', color: 'bg-emerald-100 text-emerald-700', icon: CheckCircle2 },
};

const REASON_LABELS = {
  illness: 'Illness',
  emergency: 'Emergency',
  family: 'Family Matter',
  personal: 'Personal',
  other: 'Other',
};

export default function ShiftCoverageDashboard() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [callouts, setCallouts] = useState([]);
  const [selectedCallout, setSelectedCallout] = useState(null);
  const [replacements, setReplacements] = useState([]);
  const [loadingReplacements, setLoadingReplacements] = useState(false);
  const [coverageRequests, setCoverageRequests] = useState([]);
  const [filterStatus, setFilterStatus] = useState('uncovered');

  useEffect(() => {
    fetchCallouts();
  }, [filterStatus]);

  const fetchCallouts = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (filterStatus !== 'all') {
        params.append('coverage_status', filterStatus);
      }
      
      const res = await fetch(`${API_URL}/api/coverage/callouts?${params}`, {
        credentials: 'include'
      });
      if (res.ok) {
        const data = await res.json();
        setCallouts(data.callouts || []);
      }
    } catch (err) {
      console.error('Error fetching callouts:', err);
      toast.error('Failed to load callouts');
    } finally {
      setLoading(false);
    }
  };

  const findReplacements = async (callout) => {
    setSelectedCallout(callout);
    setLoadingReplacements(true);
    setReplacements([]);
    
    try {
      const params = new URLSearchParams({
        shift_date: callout.shift_date,
      });
      if (callout.client_id) {
        params.append('client_id', callout.client_id);
      }
      if (callout.caregiver_id) {
        params.append('exclude_caregiver_id', callout.caregiver_id);
      }
      
      const res = await fetch(`${API_URL}/api/coverage/find-replacements?${params}`, {
        credentials: 'include'
      });
      
      if (res.ok) {
        const data = await res.json();
        setReplacements(data.replacements || []);
        
        // Also fetch existing coverage requests for this callout
        const reqRes = await fetch(`${API_URL}/api/coverage/coverage-requests?callout_id=${callout.id}`, {
          credentials: 'include'
        });
        if (reqRes.ok) {
          const reqData = await reqRes.json();
          setCoverageRequests(reqData.requests || []);
        }
      }
    } catch (err) {
      console.error('Error finding replacements:', err);
      toast.error('Failed to find replacements');
    } finally {
      setLoadingReplacements(false);
    }
  };

  const logCoverageContact = async (caregiver, method) => {
    try {
      const res = await fetch(`${API_URL}/api/coverage/coverage-requests`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          callout_id: selectedCallout.id,
          caregiver_id: caregiver.id,
          caregiver_name: `${caregiver.first_name} ${caregiver.last_name}`,
          contact_method: method,
        })
      });
      
      if (res.ok) {
        toast.success(`Logged ${method} contact with ${caregiver.first_name}`);
        // Refresh coverage requests
        findReplacements(selectedCallout);
      }
    } catch (err) {
      console.error('Error logging contact:', err);
    }
  };

  const updateCoverageRequestStatus = async (requestId, status, caregiverName) => {
    try {
      const res = await fetch(`${API_URL}/api/coverage/coverage-requests/${requestId}?status=${status}`, {
        method: 'PATCH',
        credentials: 'include',
      });
      
      if (res.ok) {
        if (status === 'accepted') {
          toast.success(`${caregiverName} accepted the shift! Coverage confirmed.`);
        } else {
          toast.info(`${caregiverName} ${status}`);
        }
        // Refresh data
        fetchCallouts();
        if (selectedCallout) {
          findReplacements(selectedCallout);
        }
      }
    } catch (err) {
      console.error('Error updating status:', err);
    }
  };

  const handleCall = (phone, caregiver) => {
    if (!phone) {
      toast.error('No phone number available');
      return;
    }
    // Log the contact
    logCoverageContact(caregiver, 'phone');
    // Initiate call
    window.location.href = `tel:${phone.replace(/\D/g, '')}`;
  };

  const handleEmail = (email, caregiver) => {
    if (!email) {
      toast.error('No email available');
      return;
    }
    // Log the contact
    logCoverageContact(caregiver, 'email');
    // Open email
    const subject = encodeURIComponent(`Coverage Request - ${selectedCallout?.shift_date}`);
    const body = encodeURIComponent(`Hi ${caregiver.first_name},\n\nWe have an open shift on ${selectedCallout?.shift_date} and are looking for coverage. Are you available?\n\nPlease let us know as soon as possible.\n\nThank you!`);
    window.location.href = `mailto:${email}?subject=${subject}&body=${body}`;
  };

  const getCaregiverContactStatus = (caregiverId) => {
    const request = coverageRequests.find(r => r.caregiver_id === caregiverId);
    return request?.status || 'not_contacted';
  };

  const getCaregiverRequest = (caregiverId) => {
    return coverageRequests.find(r => r.caregiver_id === caregiverId);
  };

  if (loading && callouts.length === 0) {
    return (
      <div className="min-h-screen bg-[#FBFBFA] flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-[#176DFF]" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#FBFBFA]">
      <div className="max-w-7xl mx-auto px-4 md:px-6 py-6 md:py-8">
        {/* Header */}
        <header className="mb-6">
          <button
            onClick={() => navigate('/organization')}
            className="flex items-center gap-2 text-sm text-[#64748B] hover:text-[#0D1B33] mb-4"
          >
            <ChevronLeft className="w-4 h-4" />
            Back to Organization
          </button>
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-red-500 to-amber-500 flex items-center justify-center">
                <Users className="w-6 h-6 text-white" />
              </div>
              <div>
                <h1 className="text-xl md:text-2xl font-semibold text-[#0D1B33]">Shift Coverage</h1>
                <p className="text-sm text-[#64748B]">Manage callouts and find replacements</p>
              </div>
            </div>
            <Button variant="outline" onClick={fetchCallouts}>
              <RefreshCw className="w-4 h-4 mr-2" />
              Refresh
            </Button>
          </div>
        </header>

        {/* Stats */}
        <div className="grid grid-cols-3 gap-4 mb-6">
          <button
            onClick={() => setFilterStatus('uncovered')}
            className={`p-4 rounded-xl border-2 transition-all ${
              filterStatus === 'uncovered' ? 'border-red-400 bg-red-50' : 'border-[#E2E8F0] bg-white'
            }`}
          >
            <div className="flex items-center gap-2 mb-1">
              <AlertTriangle className="w-5 h-5 text-red-500" />
              <span className="text-2xl font-bold text-[#0D1B33]">
                {callouts.filter(c => c.coverage_status === 'uncovered').length || 0}
              </span>
            </div>
            <span className="text-sm text-[#64748B]">Uncovered</span>
          </button>
          <button
            onClick={() => setFilterStatus('searching')}
            className={`p-4 rounded-xl border-2 transition-all ${
              filterStatus === 'searching' ? 'border-amber-400 bg-amber-50' : 'border-[#E2E8F0] bg-white'
            }`}
          >
            <div className="flex items-center gap-2 mb-1">
              <Search className="w-5 h-5 text-amber-500" />
              <span className="text-2xl font-bold text-[#0D1B33]">
                {callouts.filter(c => c.coverage_status === 'searching').length || 0}
              </span>
            </div>
            <span className="text-sm text-[#64748B]">Searching</span>
          </button>
          <button
            onClick={() => setFilterStatus('covered')}
            className={`p-4 rounded-xl border-2 transition-all ${
              filterStatus === 'covered' ? 'border-emerald-400 bg-emerald-50' : 'border-[#E2E8F0] bg-white'
            }`}
          >
            <div className="flex items-center gap-2 mb-1">
              <CheckCircle2 className="w-5 h-5 text-emerald-500" />
              <span className="text-2xl font-bold text-[#0D1B33]">
                {callouts.filter(c => c.coverage_status === 'covered').length || 0}
              </span>
            </div>
            <span className="text-sm text-[#64748B]">Covered</span>
          </button>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Callouts List */}
          <div className="bg-white rounded-xl border border-[#E2E8F0] overflow-hidden">
            <div className="px-5 py-4 border-b border-[#E2E8F0] bg-gray-50">
              <h2 className="font-semibold text-[#0D1B33]">Reported Callouts</h2>
            </div>
            
            {callouts.length === 0 ? (
              <div className="p-8 text-center">
                <CheckCircle2 className="w-12 h-12 text-emerald-300 mx-auto mb-3" />
                <p className="text-[#64748B]">No {filterStatus} callouts</p>
              </div>
            ) : (
              <div className="divide-y divide-[#E2E8F0] max-h-[600px] overflow-y-auto">
                {callouts.map((callout) => {
                  const statusConfig = COVERAGE_STATUS_CONFIG[callout.coverage_status] || COVERAGE_STATUS_CONFIG.uncovered;
                  const StatusIcon = statusConfig.icon;
                  
                  return (
                    <button
                      key={callout.id}
                      onClick={() => findReplacements(callout)}
                      className={`w-full p-4 text-left hover:bg-[#F8FAFC] transition-colors ${
                        selectedCallout?.id === callout.id ? 'bg-[#176DFF]/5 border-l-4 border-l-[#176DFF]' : ''
                      }`}
                    >
                      <div className="flex items-start justify-between gap-3">
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-1">
                            <span className="font-semibold text-[#0D1B33]">{callout.caregiver_name}</span>
                            <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${statusConfig.color}`}>
                              <StatusIcon className="w-3 h-3" />
                              {statusConfig.label}
                            </span>
                          </div>
                          <div className="flex flex-wrap gap-3 text-sm text-[#64748B]">
                            <span className="flex items-center gap-1">
                              <Calendar className="w-4 h-4" />
                              {new Date(callout.shift_date).toLocaleDateString()}
                            </span>
                            {callout.shift_start_time && (
                              <span className="flex items-center gap-1">
                                <Clock className="w-4 h-4" />
                                {callout.shift_start_time}
                              </span>
                            )}
                          </div>
                          {callout.client_name && (
                            <p className="text-sm text-[#64748B] mt-1">
                              Client: {callout.client_name}
                            </p>
                          )}
                          <p className="text-xs text-[#94A3B8] mt-1">
                            Reason: {REASON_LABELS[callout.reason] || callout.reason}
                          </p>
                        </div>
                        {callout.coverage_status === 'covered' && callout.coverage_caregiver_name && (
                          <div className="text-right">
                            <p className="text-xs text-emerald-600 font-medium">Covered by</p>
                            <p className="text-sm text-[#0D1B33]">{callout.coverage_caregiver_name}</p>
                          </div>
                        )}
                      </div>
                    </button>
                  );
                })}
              </div>
            )}
          </div>

          {/* Replacement Finder */}
          <div className="bg-white rounded-xl border border-[#E2E8F0] overflow-hidden">
            <div className="px-5 py-4 border-b border-[#E2E8F0] bg-gray-50">
              <h2 className="font-semibold text-[#0D1B33]">Find Replacement</h2>
            </div>
            
            {!selectedCallout ? (
              <div className="p-8 text-center">
                <Search className="w-12 h-12 text-gray-300 mx-auto mb-3" />
                <p className="text-[#64748B]">Select a callout to find replacements</p>
              </div>
            ) : loadingReplacements ? (
              <div className="p-8 text-center">
                <Loader2 className="w-8 h-8 animate-spin text-[#176DFF] mx-auto mb-3" />
                <p className="text-[#64748B]">Finding available caregivers...</p>
              </div>
            ) : (
              <div className="divide-y divide-[#E2E8F0] max-h-[600px] overflow-y-auto">
                {/* Selected callout summary */}
                <div className="p-4 bg-[#F8FAFC]">
                  <p className="text-sm text-[#64748B]">Finding coverage for:</p>
                  <p className="font-medium text-[#0D1B33]">
                    {new Date(selectedCallout.shift_date).toLocaleDateString('en-US', { weekday: 'long', month: 'short', day: 'numeric' })}
                    {selectedCallout.shift_start_time && ` at ${selectedCallout.shift_start_time}`}
                  </p>
                  {selectedCallout.client_name && (
                    <p className="text-sm text-[#64748B]">Client: {selectedCallout.client_name}</p>
                  )}
                </div>

                {replacements.length === 0 ? (
                  <div className="p-8 text-center">
                    <AlertTriangle className="w-12 h-12 text-amber-300 mx-auto mb-3" />
                    <p className="text-[#64748B]">No available caregivers found</p>
                    <p className="text-sm text-[#94A3B8] mt-1">Try adjusting availability requirements</p>
                  </div>
                ) : (
                  replacements.map((cg, idx) => {
                    const contactStatus = getCaregiverContactStatus(cg.id);
                    const request = getCaregiverRequest(cg.id);
                    
                    return (
                      <div key={cg.id} className="p-4">
                        <div className="flex items-start justify-between gap-3">
                          <div className="flex-1">
                            <div className="flex items-center gap-2 mb-1">
                              {idx === 0 && (
                                <span className="text-xs bg-amber-100 text-amber-700 px-2 py-0.5 rounded-full font-medium">
                                  Best Match
                                </span>
                              )}
                              <span className="font-semibold text-[#0D1B33]">
                                {cg.first_name} {cg.last_name}
                              </span>
                              {cg.match_score > 0 && (
                                <span className="text-xs bg-[#176DFF]/10 text-[#176DFF] px-2 py-0.5 rounded-full">
                                  {cg.match_score}% match
                                </span>
                              )}
                            </div>
                            
                            <div className="flex flex-wrap gap-2 text-xs text-[#64748B] mb-2">
                              {cg.city && (
                                <span className="flex items-center gap-1">
                                  <MapPin className="w-3 h-3" />
                                  {cg.city}, {cg.state}
                                </span>
                              )}
                              {cg.certifications?.length > 0 && (
                                <span className="flex items-center gap-1">
                                  <Award className="w-3 h-3" />
                                  {cg.certifications.join(', ')}
                                </span>
                              )}
                            </div>
                            
                            <div className="flex flex-wrap gap-1">
                              {cg.match_reasons?.map((reason, i) => (
                                <span key={i} className="text-xs bg-gray-100 text-gray-600 px-2 py-0.5 rounded">
                                  {reason}
                                </span>
                              ))}
                            </div>

                            {/* Contact Status */}
                            {contactStatus !== 'not_contacted' && (
                              <div className="mt-2">
                                <span className={`text-xs px-2 py-1 rounded-full font-medium ${
                                  contactStatus === 'accepted' ? 'bg-emerald-100 text-emerald-700' :
                                  contactStatus === 'declined' ? 'bg-red-100 text-red-700' :
                                  contactStatus === 'contacted' ? 'bg-blue-100 text-blue-700' :
                                  'bg-gray-100 text-gray-600'
                                }`}>
                                  {contactStatus === 'contacted' ? 'Contacted' :
                                   contactStatus === 'accepted' ? 'Accepted ✓' :
                                   contactStatus === 'declined' ? 'Declined' :
                                   contactStatus}
                                </span>
                              </div>
                            )}
                          </div>
                          
                          <div className="flex flex-col gap-2">
                            {/* Call Now Button */}
                            <Button
                              size="sm"
                              onClick={() => handleCall(cg.phone, cg)}
                              disabled={!cg.phone}
                              className="bg-emerald-600 hover:bg-emerald-700 text-white"
                            >
                              <PhoneCall className="w-4 h-4 mr-1" />
                              Call Now
                            </Button>
                            
                            {/* Email Button */}
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => handleEmail(cg.email, cg)}
                              disabled={!cg.email}
                            >
                              <Mail className="w-4 h-4 mr-1" />
                              Email
                            </Button>

                            {/* Status Update Buttons */}
                            {request && request.status === 'contacted' && (
                              <div className="flex gap-1 mt-1">
                                <button
                                  onClick={() => updateCoverageRequestStatus(request.id, 'accepted', `${cg.first_name} ${cg.last_name}`)}
                                  className="flex-1 text-xs px-2 py-1 bg-emerald-100 text-emerald-700 rounded hover:bg-emerald-200"
                                >
                                  Accepted
                                </button>
                                <button
                                  onClick={() => updateCoverageRequestStatus(request.id, 'declined', `${cg.first_name} ${cg.last_name}`)}
                                  className="flex-1 text-xs px-2 py-1 bg-red-100 text-red-700 rounded hover:bg-red-200"
                                >
                                  Declined
                                </button>
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    );
                  })
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
